import { BodyText, Col, LabelText, Row } from '@policygenius/mortar';
import { useState } from 'react';
import { createUseStyles } from 'react-jss';

import CheckboxGroup from 'components/formInputs/CheckboxGroup';
import HeightField from 'components/formInputs/HeightField';
import TextAreaField from 'components/formInputs/TextAreaField';
import TextField from 'components/formInputs/TextField';
import ToggleField from 'components/formInputs/ToggleField';
import FormWrapper from 'components/FormWrapper';
import ShowHide from 'components/ShowHide';
import { parseInteger } from 'forms/parsers';
import useEffectAfterMount from 'hooks/useEffectAfterMount';
import { Gender } from 'types/graph';

import { HEALTH_CONDITIONS_GROUP_LABEL, HEALTH_CONDITIONS_OPTIONS, LABELS } from './constants';
import DeclinedConditionsModal from './DeclinedConditionsModal';
import { DeclinedConditions } from './DeclinedConditionsModal/constants';
import PregnancyModal from './PregnancyModal';
import styles from './styles';
import { HealthBasicsFormProps } from './types';

const useStyles = createUseStyles(styles);
const HealthBasicsForm: React.FC<HealthBasicsFormProps> = ({
  error,
  gender,
  handleSubmit,
  invalid,
  submitting,
  values,
}) => {
  const classes = useStyles();
  const [showPregnancyModal, setShowPregnancyModal] = useState(false);
  const [declinedCondition, setDeclinedCondition] = useState<DeclinedConditions>();

  useEffectAfterMount(() => {
    if (values.planningToBeOrPregnant) {
      setShowPregnancyModal(true);
    }
  }, [values.planningToBeOrPregnant]);

  useEffectAfterMount(() => {
    if (values.alzheimerDisease) {
      setDeclinedCondition(DeclinedConditions.Alzheimers);
    }
  }, [values.alzheimerDisease]);

  useEffectAfterMount(() => {
    if (values.multipleSclerosis) {
      setDeclinedCondition(DeclinedConditions.MS);
    }
  }, [values.multipleSclerosis]);

  return (
    <>
      <FormWrapper handleSubmit={handleSubmit} invalid={invalid} submitting={submitting}>
        <Row nested>
          <Col span={{ small: 4, medium: 6 }}>
            <HeightField
              name="heightInInches"
              label={LABELS.heightInInches}
              placeholder="Choose your height"
            />
          </Col>
          <Col span={{ small: 4, medium: 6 }}>
            <TextField
              name="weight"
              label={LABELS.weight}
              config={{ parse: parseInteger }}
              suffix="lbs"
              inputMode="numeric"
            />
          </Col>
        </Row>
        <ToggleField name="historyOfTobaccoUse" groupLabel={LABELS.historyOfTobaccoUse} />
        <BodyText className={classes.exteriorHelperText}>
          Psst! Marijuana products count here, so be sure to consider that when describing your
          tobacco use history. Marijuana users are generally eligible for coverage, but insurers
          usually apply tobacco pricing. You can expect a standard drug screening as part of your
          application process.
        </BodyText>
        <ShowHide visible={!!values.historyOfTobaccoUse}>
          <ToggleField name="usedInPastYear" groupLabel={LABELS.usedInPastYear} />
        </ShowHide>
        {gender === Gender.Female && (
          <ToggleField name="planningToBeOrPregnant" groupLabel={LABELS.planningToBeOrPregnant} />
        )}
        <ToggleField name="suspendedConviction" groupLabel={LABELS.suspendedConviction} />
        <ToggleField name="dwiConviction" groupLabel={LABELS.dwiConviction} />
        <CheckboxGroup
          groupLabel={HEALTH_CONDITIONS_GROUP_LABEL}
          options={HEALTH_CONDITIONS_OPTIONS}
          name="healthConditions"
          twoColumn
        />
        <TextAreaField name="otherHealthIssues" label={LABELS.otherHealthIssues} />
        {error && (
          <LabelText className={classes.error} color="maroon" size="medium">
            {error}
          </LabelText>
        )}
      </FormWrapper>
      <PregnancyModal showModal={showPregnancyModal} setShowModal={setShowPregnancyModal} />
      <DeclinedConditionsModal
        condition={declinedCondition}
        setDeclinedCondition={setDeclinedCondition}
      />
    </>
  );
};

export default HealthBasicsForm;
